import type { Section } from '@/api/courses';
import type { HomeworkSubmission } from '@/api/progress';

import { sectionHasOpenTasks } from './sectionCompletion';
import { reviewNoteOf } from './studentFields';

/**
 * Пересдача домашки: правила без React (договор homework-resubmit.md, решения
 * владельца 11.09.2026).
 *
 * Сдача — одна строка на пару «ученик, раздел». Если куратор вернул работу «на
 * доработку» или «отклонил», ученик исправляет ответ прямо в уроке и сохраняет —
 * сервер сам возвращает работу в очередь с `resubmitted: true`. Отдельной кнопки
 * «сдать» в уроке для этого нет и не нужно.
 */

export type SectionNotice = { kind: 'revision' | 'rejected'; note: string } | { kind: 'resent' };

/** Что сказать над заданиями раздела. `null` — ничего. */
export function sectionNotice(sub: HomeworkSubmission | undefined): SectionNotice | null {
  if (!sub) return null;
  if (sub.status === 'revision' || sub.status === 'rejected') {
    return { kind: sub.status, note: reviewNoteOf(sub).trim() };
  }
  // ⚠️ Только `pending`. `resubmitted` остаётся true и после новой проверки — до
  // следующего входа в очередь; принятой работе «отправлено заново» ни к чему.
  if (sub.status === 'pending' && sub.resubmitted === true) return { kind: 'resent' };
  return null;
}

/**
 * Сдача раздела. ⚠️ Новейшая, а не первая попавшаяся: до 11.09.2026 «Сдать заново»
 * заводило вторую строку, и у старых разделов висят обе — «на доработку» и новая.
 */
export function findSectionSubmission(
  list: readonly HomeworkSubmission[] | undefined,
  sectionId: string,
): HomeworkSubmission | undefined {
  let best: HomeworkSubmission | undefined;
  for (const h of list ?? []) {
    if (h.section_id !== sectionId) continue;
    if (!best || Date.parse(h.submitted_at) > Date.parse(best.submitted_at)) best = h;
  }
  return best;
}

/** Работу вернули — её можно переделать. */
export function needsRedo(hw: Pick<HomeworkSubmission, 'status'>): boolean {
  return hw.status === 'revision' || hw.status === 'rejected';
}

/** «Переделать» — в урок и сразу на раздел сдачи. Урока сервер не нашёл — ссылки нет. */
export function lessonSectionHref(hw: Pick<HomeworkSubmission, 'lesson_id' | 'section_id'>): string | null {
  if (!hw.lesson_id) return null;
  return `/app/lessons/${encodeURIComponent(hw.lesson_id)}?section=${encodeURIComponent(hw.section_id)}`;
}

/** Урок строки, как его отдаёт react-query. */
export type RowLesson = { status: 'pending' | 'error' | 'success'; data?: { sections?: Section[] } };

/**
 * Что предложить в строке «Домашки».
 *
 * ⚠️ ОКНО «ОТПРАВИТЬ ЗАНОВО» — ТОЛЬКО ТАМ, ГДЕ В УРОКЕ ОТВЕЧАТЬ НЕГДЕ. Для раздела с
 * эссе или записью окно было второй правдой: текст уходил мимо ответа в уроке, и
 * куратор видел не то, что ученик исправил. Такой раздел переделывают в уроке.
 * Пустые заготовки заданий не в счёт — `sectionHasOpenTasks` их не считает.
 *
 * - `redo` — ссылка «Переделать»;
 * - `resubmit` — прежнее окно (раздел без заданий, урок неизвестен или закрыт);
 * - `wait` — урок грузится, чтобы не мигнуть не той кнопкой;
 * - `null` — работа на проверке или принята.
 */
export function homeworkRowAction(
  hw: HomeworkSubmission,
  lesson: RowLesson | undefined,
): 'redo' | 'resubmit' | 'wait' | null {
  if (!needsRedo(hw)) return null;
  if (!hw.lesson_id || !lesson) return 'resubmit';
  if (lesson.status === 'pending') return 'wait';
  if (lesson.status === 'error') return 'resubmit';
  const section = lesson.data?.sections?.find((s) => s.id === hw.section_id);
  return section && sectionHasOpenTasks(section) ? 'redo' : 'resubmit';
}

/** С какого раздела открыть урок: из адреса, если такой раздел есть, иначе с первого. */
export function pickInitialSection(pages: readonly { id: string }[], param: string | null): string | undefined {
  if (param && pages.some((p) => p.id === param)) return param;
  return pages[0]?.id;
}
